// # Mentor Overview Comparison Component
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  Area
} from 'recharts';
import { 
  FaUsers, 
  FaUserPlus, 
  FaMoneyBillWave, 
  FaShoppingCart,
  FaChartLine,
  FaStar,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import './mentor_overview.css';

const MentorOverview = ({ dateRange, teamMembers }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overviewData, setOverviewData] = useState({
    mentorComparison: [],
    revenueOverTime: [],
    studentDistribution: [],
    productDistribution: [],
    nationalityDistribution: [],
    performanceMetrics: [],
    topMentors: [],
    growthRate: 0
  });
  
  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  // # Fetch and process mentor overview data
  useEffect(() => {
    const fetchOverviewData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Extract all payment records related to mentorship
        let mentorshipPayments = [];
        let mentorData = {};
        
        // # Initialize data structure for each mentor
        teamMembers.forEach(mentor => {
          mentorData[mentor.id] = {
            id: mentor.id,
            name: mentor.name,
            totalRevenue: 0,
            studentCount: new Set(),
            paymentCount: 0,
            productCounts: {},
            nationalityCounts: {},
            regionCounts: {},
            monthlyRevenue: {},
            monthlyStudents: {},
            averageRating: 4.5 + Math.random() * 0.5, // Placeholder
            studentPaymentCounts: {}, // # Track how many times each student paid this mentor
            conversionRate: 60 + Math.random() * 20, // Placeholder
            responseTime: 2 + Math.random() * 3 // Placeholder in hours
          };
        });
        
        // # Process each document
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions with mentorship products
              if (paymentId.startsWith('payment_') && 
                  paymentData.transaction_type === 'payment' && 
                  paymentData.pay?.product && 
                  paymentData.pay.product.includes('mentorship')) {
                
                // # Extract payment date from metadata
                const paymentDate = new Date(paymentData.metadata?.created_at || '');
                
                // # Apply date range filter if provided
              if (dateRange) {
                const startDate = new Date(dateRange.startDate);
                const endDate = new Date(dateRange.endDate);
                
                  // # Set hours for proper date comparison
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                
                  // # Skip if outside date range
                if (paymentDate < startDate || paymentDate > endDate) {
                  return;
                }
              }
              
                // # Get mentor ID
                const mentorId = paymentData.mentor?.uid || '';
                
                // # Skip if no mentor assigned
                if (!mentorId || !mentorData[mentorId]) {
                  return;
                }
                
                // # Add payment to collection with all necessary fields - directly use stored values
                const payment = {
                  id: paymentId,
                  doc_id: docId,
                timestamp: paymentDate,
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  nationality: paymentData.user?.nationality || 'Unknown',
                  email: paymentData.user?.email || '',
                  name: paymentData.user?.name || '',
                  userId: paymentData.user?.userId || '',
                  mentor_id: mentorId,
                  mentor_name: paymentData.mentor?.mentor_name || '',
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                };
                
                mentorshipPayments.push(payment);
                
                // # Update mentor data
                const mentor = mentorData[mentorId];
                
                // # Use the pre-calculated share amount for this mentor
                const mentorShare = payment.shares[mentorId]?.net_share_egp || 0;
                mentor.totalRevenue += mentorShare;
                
                // # Track unique students
                mentor.studentCount.add(payment.email);
                
                // # Count payments
                mentor.paymentCount += 1;
                
                // # Track product distribution
                mentor.productCounts[payment.product] = (mentor.productCounts[payment.product] || 0) + 1;
                
                // # Track nationality distribution
                mentor.nationalityCounts[payment.nationality] = (mentor.nationalityCounts[payment.nationality] || 0) + 1;
                
                // # Track region distribution
                const region = getRegionFromNationality(payment.nationality);
                  mentor.regionCounts[region] = (mentor.regionCounts[region] || 0) + 1;
                  
                // # Track monthly revenue
                const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
                mentor.monthlyRevenue[monthKey] = (mentor.monthlyRevenue[monthKey] || 0) + mentorShare;
                  
                // # Track monthly students
                  if (!mentor.monthlyStudents[monthKey]) {
                    mentor.monthlyStudents[monthKey] = new Set();
                }
                mentor.monthlyStudents[monthKey].add(payment.email);
                
                // # Track student payment counts
                mentor.studentPaymentCounts[payment.email] = (mentor.studentPaymentCounts[payment.email] || 0) + 1;
              }
            });
          }
        });
        
        // # Convert mentor data to array format for display
        const mentorPerformance = Object.values(mentorData).map(mentor => ({
          ...mentor,
          studentCount: mentor.studentCount.size,
          monthlyStudents: Object.fromEntries(
            Object.entries(mentor.monthlyStudents).map(([month, students]) => [month, students.size])
          )
        }));
        
        // # Sort by total revenue
        mentorPerformance.sort((a, b) => b.totalRevenue - a.totalRevenue);
        
        // # Calculate mentor growth rates
        const mentorGrowthRates = calculateMentorGrowthRates(mentorData);
        
        // # Set the processed data
        setOverviewData({
          mentorComparison: mentorPerformance,
          revenueOverTime: mentorGrowthRates.revenueOverTime,
          studentDistribution: mentorGrowthRates.studentDistribution,
          productDistribution: mentorGrowthRates.productDistribution,
          nationalityDistribution: mentorGrowthRates.nationalityDistribution,
          performanceMetrics: mentorGrowthRates.performanceMetrics,
          topMentors: mentorGrowthRates.topMentors,
          growthRate: mentorGrowthRates.growthRate
        });
      } catch (error) {
        console.error('Error fetching mentor overview data:', error);
        setError('Failed to load mentor overview data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOverviewData();
  }, [dateRange, teamMembers]);
  
  // # Helper function to determine region from nationality
  const getRegionFromNationality = (nationality) => {
    if (!nationality || nationality === 'Unknown') {
      return 'Unknown';
    }
    
    // # Normalize the nationality string
    const normalizedNationality = nationality.toLowerCase().trim();
    
    // # Direct mapping for common countries
    const directMapping = {
      'egypt': 'Arab',
      'egyptian': 'Arab',
      'saudi arabia': 'Arab',
      'saudi': 'Arab',
      'ksa': 'Arab',
      'uae': 'Arab',
      'emirates': 'Arab',
      'kuwait': 'Arab',
      'qatar': 'Arab',
      'bahrain': 'Arab',
      'oman': 'Arab',
      'jordan': 'Arab',
      'lebanon': 'Arab',
      'syria': 'Arab',
      'iraq': 'Arab',
      'palestine': 'Arab',
      'yemen': 'Arab',
      'libya': 'Arab',
      'tunisia': 'Arab',
      'algeria': 'Arab',
      'morocco': 'Arab',
      'sudan': 'Arab',
      'usa': 'Americas',
      'united states': 'Americas',
      'america': 'Americas',
      'canada': 'Americas',
      'mexico': 'Americas',
      'brazil': 'Americas',
      'uk': 'Europe',
      'united kingdom': 'Europe',
      'england': 'Europe',
      'france': 'Europe',
      'germany': 'Europe',
      'italy': 'Europe',
      'spain': 'Europe',
      'russia': 'Europe',
      'china': 'Asia',
      'japan': 'Asia',
      'india': 'Asia',
      'pakistan': 'Asia',
      'australia': 'Oceania',
      'new zealand': 'Oceania'
    };
    
    // # Check for direct match
    if (directMapping[normalizedNationality]) {
      return directMapping[normalizedNationality];
    }
    
    // # Check for partial match
    for (const [country, region] of Object.entries(directMapping)) {
      if (normalizedNationality.includes(country)) {
        return region;
      }
    }
    
    // # Check for Arab countries
    const arabKeywords = ['arab', 'middle east', 'gcc'];
    for (const keyword of arabKeywords) {
      if (normalizedNationality.includes(keyword)) {
        return 'Arab';
      }
    }
    
    // # Return Unknown if no match found
    return 'Unknown';
  };
  
  // # Calculate mentor growth rates and other metrics
  const calculateMentorGrowthRates = (mentorData) => {
    // # Process revenue over time
    const allMonths = new Set();
    Object.values(mentorData).forEach(mentor => {
      Object.keys(mentor.monthlyRevenue).forEach(month => allMonths.add(month));
    });
    
    const sortedMonths = Array.from(allMonths).sort();
    const revenueOverTime = sortedMonths.map(month => {
      const monthData = { month };
      
      Object.values(mentorData).forEach(mentor => {
        monthData[mentor.name] = mentor.monthlyRevenue[month] || 0;
        // # Add student count for this month
        monthData[`${mentor.name}_students`] = mentor.monthlyStudents[month]?.size || 0;
      });
      
      // # Calculate total for the month
      monthData.total = Object.values(mentorData).reduce(
        (sum, mentor) => sum + (mentor.monthlyRevenue[month] || 0), 0
      );
      
      return monthData;
    });
    
    // # Calculate growth rate
    let growthRate = 0;
    if (revenueOverTime.length >= 2) {
      const currentMonth = revenueOverTime[revenueOverTime.length - 1].total;
      const previousMonth = revenueOverTime[revenueOverTime.length - 2].total;
      
      if (previousMonth > 0) {
        growthRate = ((currentMonth - previousMonth) / previousMonth) * 100;
      }
    }
    
    // # Process student distribution
    const studentDistribution = Object.values(mentorData).map(mentor => ({
      name: mentor.name,
      students: mentor.studentCount.size,
      value: mentor.studentCount.size // For pie chart
    }));
    
    // # Process product distribution
    const productDistribution = [];
    const allProducts = new Set();
    
    Object.values(mentorData).forEach(mentor => {
      Object.keys(mentor.productCounts).forEach(product => allProducts.add(product));
    });
    
    Array.from(allProducts).forEach(product => {
      const productData = { product };
      
      Object.values(mentorData).forEach(mentor => {
        productData[mentor.name] = mentor.productCounts[product] || 0;
      });
      
      productDistribution.push(productData);
    });
    
    // # Process nationality distribution
    const nationalityDistribution = [];
    const allNationalities = new Set();
    
    Object.values(mentorData).forEach(mentor => {
      Object.keys(mentor.nationalityCounts).forEach(nationality => allNationalities.add(nationality));
    });
    
    Array.from(allNationalities).forEach(nationality => {
      const nationalityData = { 
        nationality,
        region: getRegionFromNationality(nationality)
      };
      
      Object.values(mentorData).forEach(mentor => {
        nationalityData[mentor.name] = mentor.nationalityCounts[nationality] || 0;
      });
      
      nationalityDistribution.push(nationalityData);
    });
    
    // # Process performance metrics for radar chart
    const performanceMetrics = Object.values(mentorData).map(mentor => ({
      name: mentor.name,
      revenue: mentor.totalRevenue / (Math.max(...Object.values(mentorData).map(m => m.totalRevenue)) || 1) * 100,
      students: mentor.studentCount.size / (Math.max(...Object.values(mentorData).map(m => m.studentCount.size)) || 1) * 100,
      payments: mentor.paymentCount / (Math.max(...Object.values(mentorData).map(m => m.paymentCount)) || 1) * 100,
      rating: ((mentor.averageRating || 4.5) / 5) * 100, // Default to 4.5 if undefined
      conversion: mentor.conversionRate || 70 // Default to 70% if undefined
    }));
    
    // # Get top mentors
    const topMentors = Object.values(mentorData)
      .map(mentor => ({
        id: mentor.id,
        name: mentor.name,
        totalRevenue: mentor.totalRevenue,
        studentCount: mentor.studentCount.size,
        paymentCount: mentor.paymentCount
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
    
    return {
      revenueOverTime,
      studentDistribution,
      productDistribution,
      nationalityDistribution,
      performanceMetrics,
      topMentors,
      growthRate
    };
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'E£ 0.00';
    return `E£ ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  // # Format month labels
  const formatMonth = (monthKey) => {
    if (!monthKey || typeof monthKey !== 'string') return '';
    
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };
  
  // # Format percentage values
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0.0%';
    return `${Number(value).toFixed(1)}%`;
  };

  return (
    <div className="mentor_overview_container">
      {error && (
        <div className="mentor_overview_error">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="mentor_overview_loading">
          Loading mentor overview data...
        </div>
      ) : (
        <>
          {/* Overall Mentor Performance Summary */}
          <div className="mentor_overview_summary">
            <div className="mentor_overview_trend-summary">
              <div className={`mentor_overview_trend-card ${overviewData.growthRate >= 0 ? 'positive' : 'negative'}`}>
                <div className="mentor_overview_trend-icon">
                  {overviewData.growthRate >= 0 ? 
                    <FaArrowUp className="trend-icon-up" /> : 
                    <FaArrowDown className="trend-icon-down" />
                  }
                </div>
                <div className="mentor_overview_trend-content">
                  <h4>Mentor Revenue Creation Growth</h4>
                  <p className="mentor_overview_trend-value">
                    {formatPercentage(Math.abs(overviewData.growthRate))}
                  </p>
                  <p className="mentor_overview_trend-period">
                    Compared to previous month
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Top Mentors Stats */}
          <h3 className="mentor_overview_section-title">Top Performing Mentors</h3>
          <div className="mentor_overview_stats-grid">
            {overviewData.topMentors.slice(0, 3).map(mentor => (
              <div key={mentor.id} className="mentor_overview_stat-card">
                <h3>{mentor.name}</h3>
                <div className="mentor_overview_stat-content">
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">
                      <FaMoneyBillWave className="mentor_overview_stat-icon-inline" /> Revenue Created:
                    </span>
                    <span className="mentor_overview_stat-value">{formatCurrency(mentor.totalRevenue)}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">
                      <FaUsers className="mentor_overview_stat-icon-inline" /> Students:
                    </span>
                    <span className="mentor_overview_stat-value">{mentor.studentCount}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">
                      <FaShoppingCart className="mentor_overview_stat-icon-inline" /> Payments:
                    </span>
                    <span className="mentor_overview_stat-value">{mentor.paymentCount}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">
                      <FaChartLine className="mentor_overview_stat-icon-inline" /> Avg. Payment:
                    </span>
                    <span className="mentor_overview_stat-value">{formatCurrency(mentor.paymentCount > 0 ? mentor.totalRevenue / mentor.paymentCount : 0)}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">
                      <FaStar className="mentor_overview_stat-icon-inline" /> Rating *:
                    </span>
                    <span className="mentor_overview_stat-value">{(mentor.averageRating || 4.5).toFixed(1)}/5.0</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* All Mentors Comparison */}
          <h3 className="mentor_overview_section-title">All Mentors Comparison</h3>
          <div className="mentor_overview_stats-grid">
            {overviewData.mentorComparison.map(mentor => (
              <div key={mentor.id} className="mentor_overview_stat-card">
                <h3>{mentor.name}</h3>
                <div className="mentor_overview_stat-content">
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">Revenue Created:</span>
                    <span className="mentor_overview_stat-value">{formatCurrency(mentor.totalRevenue)}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">Students:</span>
                    <span className="mentor_overview_stat-value">{mentor.studentCount}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">Payments:</span>
                    <span className="mentor_overview_stat-value">{mentor.paymentCount}</span>
                  </div>
                  <div className="mentor_overview_stat-item">
                    <span className="mentor_overview_stat-label">Avg. Payment:</span>
                    <span className="mentor_overview_stat-value">{formatCurrency(mentor.paymentCount > 0 ? mentor.totalRevenue / mentor.paymentCount : 0)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Revenue Over Time Chart */}
          <div className="mentor_overview_chart-container">
            <h3>Revenue Creation Trends Over Time</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={overviewData.revenueOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={formatMonth}
                  label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  yAxisId="left"
                  label={{ value: 'Revenue Created (EGP)', angle: -90, position: 'insideLeft' }}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  label={{ value: 'Students', angle: 90, position: 'insideRight' }}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name.includes('_students')) {
                      return [value, `${name.replace('_students', '')} Students`];
                    }
                    return [formatCurrency(value), name];
                  }}
                  labelFormatter={formatMonth}
                />
                <Legend />
                {overviewData.mentorComparison.map((mentor, index) => (
                  <Line 
                    key={mentor.id}
                    type="monotone"
                    dataKey={mentor.name}
                    yAxisId="left"
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
                {overviewData.mentorComparison.map((mentor, index) => (
                  <Bar 
                    key={`${mentor.id}_students`}
                    dataKey={`${mentor.name}_students`}
                    yAxisId="right"
                    fill={COLORS[index % COLORS.length]}
                    fillOpacity={0.3}
                    stackId="students"
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          
          {/* Revenue Comparison Chart */}
          <div className="mentor_overview_chart-container">
            <h3>Revenue Creation Comparison</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={overviewData.mentorComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value, name) => {
                  if (name === 'totalRevenue') return [formatCurrency(value), 'Total Revenue Created'];
                  if (name === 'averageRevenue') return [formatCurrency(value), 'Avg. Revenue per Payment'];
                  return [value, name];
                }} />
                <Legend />
                <Bar dataKey="totalRevenue" fill="#4a90e2" name="Total Revenue Created" />
                <Bar dataKey="averageRevenue" fill="#82ca9d" name="Avg. Revenue per Payment" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Student Distribution Chart */}
          <div className="mentor_overview_chart-container">
            <h3>Student Distribution by Mentor</h3>
            <div className="mentor_overview_chart-grid">
              <div className="mentor_overview_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={overviewData.studentDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    >
                      {overviewData.studentDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Students']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mentor_overview_chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={overviewData.studentDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [value, 'Students']} />
                    <Bar dataKey="students" fill="#8884d8" name="Students" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Performance Metrics Radar Chart */}
          <div className="mentor_overview_chart-container">
            <h3>Mentor Performance Metrics</h3>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart outerRadius={150} data={overviewData.performanceMetrics}>
                <PolarGrid />
                <PolarAngleAxis dataKey="name" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="Revenue Created" dataKey="revenue" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Radar name="Students" dataKey="students" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                <Radar name="Payments" dataKey="payments" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                <Radar name="Rating **" dataKey="rating" stroke="#ff8042" fill="#ff8042" fillOpacity={0.6} />
                <Radar name="Completion" dataKey="completion" stroke="#0088FE" fill="#0088FE" fillOpacity={0.6} />
                <Legend />
                <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, '']} />
              </RadarChart>
            </ResponsiveContainer>
            <p className="mentor_overview_note">* Rating is prototype value for demonstration. Completion Rate shows mentees who chose the same mentor again.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default MentorOverview;